import { Trans, useLingui } from "@lingui/react/macro";
import { Gift, Mail, X } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { PickerCountry } from "./use-countries-query";

interface CountryComingSoonBannerProps {
  country: PickerCountry;
  onDismiss: () => void;
  onOpenWaitlist: () => void;
  onOpenReferral: () => void;
}

/**
 * Footer banner for `coming_soon` countries the user previewed in the picker.
 * Renders one of four variants based on the country's flags (per
 * `_docs/country-launch-plan.md`):
 *
 *   waitlist + referral → "Get notified" CTA + referral teaser
 *   waitlist only       → "Get notified" CTA
 *   referral only       → referral teaser
 *   neither             → "On the roadmap" passive notice
 *
 * The X dismisses the banner without changing the user's stored country.
 */
export function CountryComingSoonBanner({
  country,
  onDismiss,
  onOpenWaitlist,
  onOpenReferral,
}: CountryComingSoonBannerProps) {
  const { i18n } = useLingui();
  const displayName =
    country.name[i18n.locale] || country.name.en || country.name.ro || country.code;

  const showWaitlist = country.waitlistEnabled;
  const showReferral = country.referralEnabled;
  const showRoadmap = !showWaitlist && !showReferral;

  return (
    <div className="bg-card border-border relative w-full border-t">
      <button
        type="button"
        onClick={onDismiss}
        className="text-muted-foreground hover:text-foreground hover:bg-muted absolute top-2 right-2 flex size-8 items-center justify-center rounded-full transition-colors"
        aria-label="Dismiss"
      >
        <X className="size-4" />
      </button>

      <div className="flex flex-col gap-3 px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center gap-3 pr-10">
          <span className="text-2xl">{country.flag}</span>
          <div className="min-w-0 flex-1">
            <h3 className="text-foreground truncate text-base font-semibold">{displayName}</h3>
            <p className="text-muted-foreground text-xs">
              <Trans>Coming soon — not yet bookable.</Trans>
            </p>
          </div>
        </div>

        {showRoadmap && (
          <p className="text-muted-foreground text-sm">
            <Trans>{displayName} is on our roadmap. We'll announce when bookings open.</Trans>
          </p>
        )}

        {(showWaitlist || showReferral) && (
          <div className="flex flex-col gap-2 sm:flex-row">
            {showWaitlist && (
              <Button
                type="button"
                variant="default"
                onClick={onOpenWaitlist}
                className="w-full justify-center gap-2 sm:flex-1"
              >
                <Mail className="size-4" />
                <Trans>Get notified when live</Trans>
              </Button>
            )}
            {showReferral && (
              <Button
                type="button"
                variant="outline"
                onClick={onOpenReferral}
                className="w-full justify-center gap-2 sm:flex-1"
              >
                <Gift className="size-4" />
                <Trans>Earn from referrals</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
